import { useTranslation } from 'react-i18next';
import type { ActiveOrder } from '@/mocks/dashboard';

interface Props {
  orders: ActiveOrder[];
}

const paymentColors: Record<string, string> = {
  pending: 'bg-warm-50 text-warm-700 border-warm-200',
  paid: 'bg-ink-50 text-ink-600 border-ink-200',
  partial: 'bg-warm-50 text-warm-700 border-warm-200',
  refunded: 'bg-ink-50 text-ink-400 border-ink-200',
};

const productionColors: Record<string, string> = {
  not_started: 'bg-ink-50 text-ink-400 border-ink-200',
  in_progress: 'bg-warm-50 text-warm-700 border-warm-200',
  completed: 'bg-ink-50 text-ink-600 border-ink-200',
};

const shippingColors: Record<string, string> = {
  not_shipped: 'bg-ink-50 text-ink-400 border-ink-200',
  packing: 'bg-warm-50 text-warm-700 border-warm-200',
  shipped: 'bg-warm-50 text-warm-700 border-warm-200',
  delivered: 'bg-ink-50 text-ink-600 border-ink-200',
};

export default function DashboardOrders({ orders }: Props) {
  const { t } = useTranslation('dashboard');

  return (
    <div className="rounded-xl border border-ink-100 bg-white">
      <div className="flex items-center justify-between px-4 md:px-5 py-3 md:py-4 border-b border-ink-100">
        <div className="flex items-center gap-2">
          <i className="ri-box-3-line text-warm-400 text-lg" />
          <h3 className="text-sm md:text-base font-semibold text-ink-600">{t('dashboard.orders.title')}</h3>
        </div>
        <a href="/my/orders" className="text-xs md:text-sm font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap">
          {t('dashboard.seeAll')}
        </a>
      </div>

      {orders.length === 0 ? (
        <div className="px-4 md:px-5 py-8 text-center">
          <div className="w-12 h-12 rounded-full bg-cream-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-box-3-line text-cream-400 text-xl" />
          </div>
          <p className="text-sm text-ink-300">{t('dashboard.orders.empty')}</p>
        </div>
      ) : (
        <div className="divide-y divide-ink-100">
          {orders.map((o) => (
            <div key={o.id} className="px-4 md:px-5 py-3 md:py-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-warm-400">{o.orderNumber}</span>
                    <span className="text-xs text-ink-300">ref {o.quoteNumber}</span>
                  </div>
                  <p className="text-sm font-medium text-ink-600 mt-1 truncate">{o.productSummary}</p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${paymentColors[o.paymentStatus]}`}>
                      {t(`dashboard.paymentStatus.${o.paymentStatus}`)}
                    </span>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${productionColors[o.productionStatus]}`}>
                      {t(`dashboard.productionStatus.${o.productionStatus}`)}
                    </span>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${shippingColors[o.shippingStatus]}`}>
                      {t(`dashboard.shippingStatus.${o.shippingStatus}`)}
                    </span>
                  </div>
                  {o.estimatedDelivery && (
                    <p className="text-xs text-ink-300 mt-1.5">
                      {t('dashboard.orders.delivery')}: {o.estimatedDelivery}
                    </p>
                  )}
                </div>
                <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 sm:gap-1 shrink-0">
                  <span className="text-sm font-semibold text-ink-600">
                    {o.currency} {o.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                  <a
                    href={`/my/orders/${o.id}`}
                    className="text-xs font-medium text-warm-400 hover:text-warm-500 transition-colors whitespace-nowrap"
                  >
                    {t('dashboard.orders.view')} →
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}